import React, { useEffect, useState } from "react";
import { X, Printer, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/api";

const money = (n) => {
  const v = Number(n || 0);
  return `£${v.toLocaleString("en-GB", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
};
const when = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

const spent = (a, b) => {
  const from = new Date(a).getTime();
  const to = new Date(b).getTime();
  if (!from || !to || to <= from) return null;
  const mins = Math.round((to - from) / 60000);
  if (mins < 1) return "under a minute";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"}`;
  const h = Math.floor(mins / 60);
  return `${h} hour${h === 1 ? "" : "s"} ${mins % 60} min`;
};

const outcome = (j) =>
  j.converted
    ? j.status === "consultation"
      ? "Left details · needed a call"
      : "Left details"
    : j.status === "consultation"
      ? "Needed a call"
      : "Walked away";

const Row = ({ label, value }) => (
  <div className="flex gap-3 py-1.5 border-b border-slate-50 last:border-0 text-sm">
    <span className="text-slate-500 w-52 shrink-0">{label}</span>
    <span className="text-navy font-medium break-words">{value}</span>
  </div>
);

const printable = (j) => {
  const q = j.quote || {};
  const t = q.totals || {};
  const esc = (v) => String(v ?? "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
  const block = (title, rows) =>
    `<h2>${esc(title)}</h2><table>${rows.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join("")}</table>`;
  const services = (q.services || [])
    .map(
      (s) =>
        `<h3>${esc(s.name)}</h3><table>${(s.params || [])
          .map((p) => `<tr><th>${esc(p.label)}</th><td>${esc(p.value)}</td></tr>`)
          .join("")}</table>`,
    )
    .join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>Pricing journey ${esc(j.session_id)}</title>
<style>
body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;margin:32px;font-size:12px}
h1{font-size:18px;margin:0 0 4px}h2{font-size:13px;margin:22px 0 6px;text-transform:uppercase;letter-spacing:.05em;color:#64748b}
h3{font-size:12px;margin:14px 0 4px}
table{width:100%;border-collapse:collapse;margin:0}
th{text-align:left;font-weight:normal;color:#64748b;width:45%;padding:3px 8px 3px 0;vertical-align:top}
td{padding:3px 0;vertical-align:top}
tr{border-bottom:1px solid #f1f5f9}
.muted{color:#64748b;margin:0 0 18px}
</style></head><body>
<h1>Pricing journey</h1>
<p class="muted">Reference ${esc(j.session_id)} · built ${esc(when(j.created_at))} · printed ${esc(new Date().toLocaleString("en-GB"))}</p>
${block("The price they were shown", [
    ["Monthly (net)", money(t.monthly_recurring ?? j.monthly_net)],
    ["One-off (net)", money(t.one_off ?? j.one_off_net)],
    ["Outcome", outcome(j)],
    ["Pricing version", j.pricing_version || "—"],
  ])}
${block("Their journey", [
    ["First price", when(j.created_at)],
    ["Last change", when(j.updated_at)],
    ["Time on the tool", spent(j.created_at, j.updated_at) || "—"],
    ["Times they changed the answers", j.quote_count ? `${j.quote_count} price${j.quote_count === 1 ? "" : "s"} worked out` : "—"],
  ])}
${
  j.converted
    ? block("Who they are", [
        ["Name", j.full_name || "—"],
        ["Business", j.business_name || "—"],
        ["Email", j.email || "—"],
        ["Phone", j.phone || "—"],
        ["Message", j.message || "—"],
      ])
    : ""
}
<h2>What they asked for</h2>${services || "<p>No services recorded.</p>"}
${(q.consultation_reasons || []).length ? block("Why it needed a call", (q.consultation_reasons || []).map((r, i) => [`Reason ${i + 1}`, r])) : ""}
${(q.review_reasons || []).length ? block("Flagged for review", (q.review_reasons || []).map((r, i) => [`Reason ${i + 1}`, r])) : ""}
</body></html>`;
};

export const PackageJourneyModal = ({ sessionId, onClose }) => {
  const [j, setJ] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setJ(null);
    setError("");
    adminApi.packageBuilder
      .session(sessionId)
      .then(setJ)
      .catch(() => setError("Couldn't load this journey."));
  }, [sessionId]);

  const print = () => {
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    w.document.write(printable(j));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const q = j?.quote || {};
  const t = q.totals || {};
  const services = q.services || [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-navy/50 backdrop-blur-sm p-4 overflow-y-auto" data-testid="journey-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-100">
          <div>
            <h3 className="font-display font-bold text-navy text-lg">What they asked for</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Everything this visitor put into the pricing tool before {j?.converted ? "leaving their details" : "leaving"}.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {j && (
              <button onClick={print} data-testid="journey-modal-print" className="inline-flex items-center gap-2 border border-slate-200 hover:border-orange hover:text-orange-dark text-slate-500 font-semibold text-sm px-3 py-1.5 rounded-full transition-colors">
                <Printer size={14} /> Print / PDF
              </button>
            )}
            <button onClick={onClose} data-testid="journey-modal-close" className="text-slate-400 hover:text-navy p-1"><X size={20} /></button>
          </div>
        </div>

        <div className="p-5">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!j && !error && <p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Loading…</p>}
          {j && (
            <>
              <div className="grid sm:grid-cols-3 gap-3 mb-5">
                {[
                  ["Monthly", money(t.monthly_recurring ?? j.monthly_net)],
                  ["One-off", money(t.one_off ?? j.one_off_net)],
                  ["Outcome", outcome(j)],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-slate-50 px-4 py-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">{l}</p>
                    <p className="text-navy font-display font-bold">{v}</p>
                  </div>
                ))}
              </div>

              <h4 className="text-xs uppercase tracking-wide text-slate-400 font-bold mb-1">Their journey</h4>
              <div className="mb-5" data-testid="journey-modal-behaviour">
                <Row label="First price" value={when(j.created_at)} />
                <Row label="Last change" value={when(j.updated_at)} />
                <Row label="Time on the tool" value={spent(j.created_at, j.updated_at) || "Only one visit recorded"} />
                <Row
                  label="Prices they worked out"
                  value={j.quote_count ? `${j.quote_count}${j.quote_count > 1 ? " — they changed their answers" : " — first price only"}` : "Not recorded for this journey"}
                />
              </div>

              {j.converted && (
                <>
                  <h4 className="text-xs uppercase tracking-wide text-slate-400 font-bold mb-1">Who they are</h4>
                  <div className="mb-5">
                    <Row label="Name" value={j.full_name || "—"} />
                    <Row label="Business" value={j.business_name || "—"} />
                    <Row label="Email" value={j.email || "—"} />
                    <Row label="Phone" value={j.phone || "—"} />
                    {j.message && <Row label="Message" value={j.message} />}
                  </div>
                </>
              )}

              <h4 className="text-xs uppercase tracking-wide text-slate-400 font-bold mb-1">Services and answers</h4>
              {services.length === 0 ? (
                <p className="text-sm text-slate-400">No services were recorded for this journey.</p>
              ) : (
                <div className="space-y-4" data-testid="journey-modal-services">
                  {services.map((s, i) => (
                    <div key={i} className="rounded-xl border border-slate-100 p-4">
                      <p className="font-display font-bold text-navy text-sm">{s.name}</p>
                      {(s.params || []).length === 0 ? (
                        <p className="text-xs text-slate-400 mt-1">No questions on this service.</p>
                      ) : (
                        <div className="mt-2">
                          {s.params.map((p, k) => <Row key={k} label={p.label} value={String(p.value)} />)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {(q.consultation_reasons || []).length > 0 && (
                <div className="mt-5 rounded-xl bg-navy/5 p-4">
                  <p className="text-xs uppercase tracking-wide text-navy font-bold mb-2">Why it needed a call</p>
                  <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1">
                    {q.consultation_reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
              {(q.review_reasons || []).length > 0 && (
                <div className="mt-3 rounded-xl bg-orange/10 p-4">
                  <p className="text-xs uppercase tracking-wide text-orange-dark font-bold mb-2">Flagged for review</p>
                  <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1">
                    {q.review_reasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}

              <p className="text-[11px] text-slate-400 mt-5">Reference {j.session_id}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackageJourneyModal;
